import {
  ACCOUNTING_CONFIG,
  IPaymentPlanConfig,
  PaymentPlanBrandingThemeFilterType,
  PaymentPlanFirstPaymentDate,
  PaymentPlanFrequency,
  PaymentPlanSurchargeUnit,
  paymentPlanConfigFields,
} from 'shared';
import { useMemo, useState } from 'react';
import { MenuItem, TextField } from '@mui/material';

import { Button } from '../../../../../common/Atoms/Button';
import { InputTextAddon } from '../../../../../common/Atoms/InputTextAddon';
import { CurrencyInput } from '../../../../../common/Atoms/CurrencyInput';
import { Select } from '../../../../../common/Atoms/Select';
import { CheckBox } from '../../../../../common/Atoms/CheckBox';
import { LoadingOverlay } from '../../../Molecules/LoadingOverlay';
import { AccountSelector } from '../../../Settings/Policies/PolicyForm/AccountSelector';
import { FormLabel } from '../../../../../common/Atoms/FormLabel';
import { MultiSelect } from '../../../../../common/Atoms/MultiSelect';
import { useAccountingResources } from '../../../../hooks/useAccountingResources';
import { useAccountingSystemSetting } from '../../../../hooks/useAccountingSystemSetting';

interface PaymentPlanConfigFormProps {
  config: IPaymentPlanConfig;
  onSave?: (config: IPaymentPlanConfig) => void;
  onDelete?: (id: number) => void;
  loading?: boolean;
}

const frequencyTypeOptions = [{
  title: `Weekly`,
  value: PaymentPlanFrequency.WEEKLY,
}, {
  title: `Fortnightly`,
  value: PaymentPlanFrequency.FORTNIGHTLY,
}, {
  title: `4 Weekly`,
  value: PaymentPlanFrequency.FOURWEEKLY,
}, {
  title: `Monthly`,
  value: PaymentPlanFrequency.MONTHLY,
}, {
  title: `Bi-monthly`,
  value: PaymentPlanFrequency.BIMONTHLY,
}, {
  title: `Quarterly`,
  value: PaymentPlanFrequency.QUARTERLY,
}];

export function PaymentPlanConfigForm({
  config,
  onSave,
  onDelete,
  loading,
}: PaymentPlanConfigFormProps) {
  const [form, setForm] = useState<IPaymentPlanConfig>(config);
  const [isDirty, setIsDirty] = useState(false);
  const hideInvoiceBranding = useAccountingSystemSetting(ACCOUNTING_CONFIG.HIDE_INVOICE_BRANDING);
  const { data: resources, isLoading: resourcesLoading } = useAccountingResources();

  const brandingThemeOptions = useMemo(() => {
    return (resources?.brandingThemes || []).map(theme => ({
      label: theme.name,
      value: theme.brandingThemeID,
    }));
  }, [resources?.brandingThemes]);

  const brandingThemeFilterOptions = [
    { label: `Disabled`, value: `` },
    { label: `Only include`, value: PaymentPlanBrandingThemeFilterType.Include },
    { label: `Exclude`, value: PaymentPlanBrandingThemeFilterType.Exclude },
  ];

  function getFirstPaymentDateLabel(value: PaymentPlanFirstPaymentDate): string {
    switch (value) {
    case PaymentPlanFirstPaymentDate.Now:
      return `Start today`;
    case PaymentPlanFirstPaymentDate.DueDate:
      return `On invoice due date`;
    case PaymentPlanFirstPaymentDate.NowPlusFrequency:
      return `Start after one interval`;
    default:
      return String(value);
    }
  }

  const filterFieldNames = useMemo(() => {
    return Object.entries(paymentPlanConfigFields)
      .filter(([, meta]) => meta.type === `filter`)
      .map(([key]) => key);
  }, []);

  const firstPaymentDateOptions = useMemo(() => {
    return Object.values(PaymentPlanFirstPaymentDate).map(v => ({
      label: getFirstPaymentDateLabel(v),
      value: v,
    }));
  }, []);

  const surchargeUnitOptions = useMemo(() => {
    return [
      { label: `Disabled`, value: `` },
      { label: `Percent`, value: PaymentPlanSurchargeUnit.Percent },
      { label: `Fixed amount`, value: PaymentPlanSurchargeUnit.FixedAmountCents },
    ];
  }, []);

  function handleNumberChange(field: keyof IPaymentPlanConfig) {
    return ({ value }: { value: string | number }) => {
      const num = typeof value === `number` ? value : Number(value ?? 0);

      setForm(prev => ({
        ...prev,
        [field]: Number.isFinite(num) ? num : 0,
      }));

      setIsDirty(true);
    };
  }

  function handleDecimalChange(field: keyof IPaymentPlanConfig) {
    return ({ value }: { value: string | number }) => {
      setForm(prev => ({
        ...prev,
        [field]: value,
      }));

      setIsDirty(true);
    };
  }

  function handleSelectChange(field: keyof IPaymentPlanConfig) {
    return ({ value }: { value: string }) => {
      setForm(prev => ({
        ...prev,
        [field]: value,
      }));

      setIsDirty(true);
    };
  }

  function handleFrequencyTypeChange({ value }: { value: string }) {
    const frequencyType = value as PaymentPlanFrequency;

    setForm(prev => ({
      ...prev,
      frequencyType,
    }));

    setIsDirty(true);
  }

  function handleBrandingThemeFilterTypeChange({ value }: { value: string }) {
    const brandingThemeFilterType = value === `` ? null : (value as PaymentPlanBrandingThemeFilterType);

    setForm(prev => ({
      ...prev,
      brandingThemeFilterType,
      brandingThemeIds: brandingThemeFilterType ? prev.brandingThemeIds : null,
    }));

    setIsDirty(true);
  }

  function handleBrandingThemeIdsChange(value: string[] | number[]) {
    setForm(prev => ({
      ...prev,
      brandingThemeIds: value.map(String),
    }));

    setIsDirty(true);
  }

  function handleSurchargeUnitChange({ value }: { value: string }) {
    const unit =
      value === `` ? null : (value as PaymentPlanSurchargeUnit);

    setForm(prev => ({
      ...prev,
      surchargeUnit: unit,
    }));

    setIsDirty(true);
  }

  function handleFirstPaymentDateToggle(value: PaymentPlanFirstPaymentDate, checked: boolean) {
    setForm(prev => {
      const existing = prev.firstPaymentDates || [];
      const next = checked
        ? Array.from(new Set([...existing, value]))
        : existing.filter(v => v !== value);

      // Keep selected values in option order for stable API payloads/UI rendering.
      const ordered = firstPaymentDateOptions
        .map(option => option.value as PaymentPlanFirstPaymentDate)
        .filter(optionValue => next.includes(optionValue));

      return {
        ...prev,
        firstPaymentDates: ordered,
      };
    });

    setIsDirty(true);
  }

  function handleSave() {
    if (onSave) {
      onSave(form);
      setIsDirty(false);
    }
  }

  function handleDelete() {
    if (onDelete) {
      onDelete(config.id);
    }
  }

  return (
    <LoadingOverlay
      loading={ loading }
    >
      <div className={ `border border-gray-200 rounded-md p-4 mt-4 bg-white` }>
        <div className={ `grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-6 items-stretch` }>
          <div className={ `` }>
            <div className={ `text-sm font-medium text-gray-900 mb-2` }>{ `Setup` }</div>
            <div className={ `mt-4` }>
              <div className={ `flex items-center gap-2 whitespace-nowrap overflow-x-auto text-sm text-gray-700` }>
                <span>{ `Allow customers to split invoices into a maximum of` }</span>
                <TextField
                  size={ `small` }
                  type={ `number` }
                  name={ `maxNumberOfPayments` }
                  value={ form.maxNumberOfPayments ?? `` }
                  onChange={ e => {
                    const maxNumberOfPayments = Number(e.target.value ?? 0);

                    setForm(prev => ({
                      ...prev,
                      maxNumberOfPayments: Number.isFinite(maxNumberOfPayments) ? maxNumberOfPayments : 0,
                    }));

                    setIsDirty(true);
                  } }
                  slotProps={ { htmlInput: { min: 1 } } }
                  sx={ { width: 92, '& .MuiInputBase-input': { py: 0.75 } } }
                />
                <TextField
                  select
                  size={ `small` }
                  name={ `frequencyType` }
                  value={ form.frequencyType }
                  onChange={ e => handleFrequencyTypeChange({ value: e.target.value }) }
                  sx={ { width: 170, '& .MuiInputBase-input': { py: 0.75 } } }
                >
                  { frequencyTypeOptions.map(option => (
                    <MenuItem
                      key={ option.value }
                      value={ option.value }
                    >
                      { option.title }
                    </MenuItem>
                  )) }
                </TextField>
                <span>{ `payments` }</span>
              </div>
            </div>

            <div className={ `mt-4` }>
              <FormLabel>{ `Allowed First Payment Dates` }</FormLabel>
              <div className={ `mt-1 text-xs text-gray-500` }>
                { `The allowed options for the first payment date of the payment plan.` }
              </div>
              <div className={ `mt-2 space-y-2` }>
                { firstPaymentDateOptions.map(option => (
                  <CheckBox
                    key={ option.value }
                    name={ option.value }
                    label={ option.label }
                    checked={ (form.firstPaymentDates || []).includes(option.value as PaymentPlanFirstPaymentDate) }
                    onChange={ ({ value }) => handleFirstPaymentDateToggle(option.value as PaymentPlanFirstPaymentDate, value) }
                    placement={ `left` }
                  />
                )) }
              </div>
            </div>

            <div className={ `mt-4` }>
              <Select
                label={ `Surcharge` }
                name={ `surchargeUnit` }
                options={ surchargeUnitOptions }
                selected={ form.surchargeUnit ?? `` }
                onChange={ handleSurchargeUnitChange }
                helpIcon={ `Optional surcharge applied to the invoice when payment plan created.` }
              />
              { form.surchargeUnit && (
                <div className={ `mt-2 ml-2 pl-3 border-l border-gray-200 flex items-end` }>
                  { form.surchargeUnit === PaymentPlanSurchargeUnit.Percent && (
                    <InputTextAddon
                      className={ `max-w-[200px]` }
                      label={ `Surcharge Percent` }
                      name={ `surchargePercent` }
                      value={ form.surchargePercent || 0 }
                      addOnText={ `%` }
                      onChange={ handleDecimalChange(`surchargePercent`) }
                      helpIcon={ `Percent of invoice total to be added to the invoice` }
                    />
                  ) }
                  { form.surchargeUnit === PaymentPlanSurchargeUnit.FixedAmountCents && (
                    <CurrencyInput
                      label={ `Surcharge Amount` }
                      name={ `surchargeCents` }
                      className={ `max-w-[200px]` }
                      value={ form.surchargeCents || 0 }
                      onChange={ handleNumberChange(`surchargeCents`) }
                      helpIcon={ `Fixed surcharge amount. This is the total amount for ALL installments.` }
                    />
                  ) }
                  <AccountSelector
                    className={ `ml-2` }
                    label={ `Surcharge Revenue Account` }
                    name={ `externalSurchargeAccountCode` }
                    value={ form.externalSurchargeAccountCode || null }
                    onChange={ handleSelectChange(`externalSurchargeAccountCode`) }
                    helpIcon={ `Select the revenue account to record surcharge income against.` }
                  />
                </div>
              ) }
            </div>
          </div>

          <div
            className={ `hidden md:block w-px bg-gray-200 self-stretch` }
            aria-hidden={ true }
          />

          <div className={ `` }>
            <div className={ `text-sm font-medium text-gray-900 mb-2` }>{ `Filters` }</div>
            <div className={ `grid grid-cols-1 md:grid-cols-2 gap-4` }>
              { filterFieldNames.includes(`minInvoiceDaysOverdue`) && (
                <InputTextAddon
                  label={ `Min Invoice Days Overdue` }
                  name={ `minInvoiceDaysOverdue` }
                  value={ form.minInvoiceDaysOverdue ?? `` }
                  dataType={ `number` }
                  onChange={ handleNumberChange(`minInvoiceDaysOverdue`) }
                  helpIcon={ `Only allow plans for invoices overdue by at least this many days. Use negative amounts for before due.` }
                />
              ) }
              { filterFieldNames.includes(`maxInvoiceDaysOverdue`) && (
                <InputTextAddon
                  label={ `Max Invoice Days Overdue` }
                  name={ `maxInvoiceDaysOverdue` }
                  value={ form.maxInvoiceDaysOverdue ?? `` }
                  dataType={ `number` }
                  onChange={ handleNumberChange(`maxInvoiceDaysOverdue`) }
                  helpIcon={ `Only allow plans for invoices overdue by at most this many days. Use negative amounts for before due.` }
                />
              ) }
            </div>
            { !hideInvoiceBranding && filterFieldNames.includes(`brandingThemeFilterType`) && (
              <div className={ `mt-4` }>
                <Select
                  label={ `Branding Themes` }
                  name={ `brandingThemeFilterType` }
                  options={ brandingThemeFilterOptions }
                  selected={ form.brandingThemeFilterType ?? `` }
                  onChange={ handleBrandingThemeFilterTypeChange }
                  helpIcon={ `Only allow plans for invoices that use (or do not use) the selected branding themes.` }
                />
                { form.brandingThemeFilterType && (
                  <div className={ `mt-2 ml-2 pl-3 border-l border-gray-200` }>
                    <MultiSelect
                      options={ brandingThemeOptions }
                      selected={ form.brandingThemeIds || [] }
                      onChange={ handleBrandingThemeIdsChange }
                      loading={ resourcesLoading }
                      display={ form.brandingThemeIds?.length ? `${form.brandingThemeIds.length} selected` : `None Selected` }
                    />
                  </div>
                ) }
              </div>
            ) }
          </div>
        </div>
        <div className={ `flex items-center justify-between mt-4` }>
          <span className={ `text-xs text-gray-600` }>
            { `Payment Plans are enabled while this configuration exists. Delete it to disable Payment Plans for this group.` }
          </span>
          <div className={ `flex gap-2` }>
            <Button
              color={ `paidniceRed` }
              onClick={ handleDelete }
            >
              { `Delete & Disable` }
            </Button>
            <Button
              onClick={ handleSave }
              disabled={ !isDirty }
            >
              { `Save` }
            </Button>
          </div>
        </div>
      </div>
    </LoadingOverlay>
  );
}
